import React, { useState, useEffect, useRef, useCallback, InputHTMLAttributes, TextareaHTMLAttributes } from 'react';
import { createPortal } from 'react-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { axiosCaller } from '../../utils/apiCaller';
import './MentionInput.scss';

/**
 * One row from GET /api/users/mentionable. Narrow shape — the endpoint
 * deliberately projects just these fields to avoid leaking sensitive data
 * (email, permissions, etc.) into autocomplete responses.
 */
export interface MentionableUser {
    id: number;
    username: string;
    firstname: string;
    lastname: string;
    avatar: string | null;
}

/**
 * Standard input props — caller controls the input's value just like any
 * controlled `<input>` or `<textarea>`. MentionInput is a transparent wrapper
 * that adds @ autocomplete on top; the caller doesn't need to know about
 * mentions to use it as a regular input.
 *
 * `onChange` here mirrors the React shape (event-based) for drop-in
 * compatibility with existing call sites. `onKeyDown` passes through to
 * the caller AFTER we handle mention-mode keys (ArrowUp/Down/Enter/Tab/Esc
 * are swallowed when the popover is open; other keys reach the caller
 * unchanged so Enter-to-submit on the form still works).
 *
 * When `multiline` is set, the component renders a `<textarea>` instead of
 * an `<input>`. All other behavior — popover positioning, keyboard nav, the
 * @ detection / insertion logic — is shared between the two. Surfaces with
 * long comments (measure / agreement / installation / servicing / transport
 * detail pages) prefer the textarea variant; the original input variant
 * stays the default so existing call sites are unchanged.
 */
type CommonProps = {
    value: string;
    onChange: (next: string) => void;
};

type InputProps = CommonProps & Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'> & {
    multiline?: false;
};

type TextareaProps = CommonProps & Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'onChange' | 'value'> & {
    multiline: true;
};

type Props = InputProps | TextareaProps;

/** Characters allowed in a mention prefix — matches ByMentionResolver's PHP regex. */
const MENTION_CHAR_RE = /[a-zA-Z0-9_.-]/;

/** Debounce window for the autocomplete fetch — small enough that typing feels live. */
const FETCH_DEBOUNCE_MS = 180;

/**
 * @mention-aware controlled input. Wraps a regular HTML input; when the user
 * types `@` (at start-of-string or after whitespace), a portal popover opens
 * with company users, debounce-filtered by what they type. Arrow keys / Enter
 * / Tab / Esc / mouse all work as expected; selecting a user inserts
 * `@username ` at the caret and restores focus.
 *
 * Design choices worth knowing:
 *   - **createPortal** (matching TimeDropdown's pattern): the popover renders
 *     into document.body and positions via getBoundingClientRect so it can
 *     escape clipping parents (e.g. the production board's overflow:hidden
 *     ExpansionPanel).
 *   - **Position is computed on open and re-computed on scroll/resize** so
 *     the popover stays anchored to the input through scroll/keyboard motion.
 *   - **No autocomplete fetching when not in mention mode** — fetches are
 *     debounced AND only triggered while mentionActive is true. Latest
 *     response always wins (the fetcher checks mentionActive at resolution
 *     time and bails if the state has moved on).
 *   - **Self-exclusion is handled server-side** (the endpoint filters out
 *     the caller). No client-side filter needed.
 */
const MentionInput: React.FC<Props> = (props) => {
    // Destructure here (not in the param list) so we can keep the shared
    // logic type-clean. multiline is treated as a discriminator; the rest
    // of the file works against the union via HTMLInputElement | HTMLTextAreaElement.
    const { value, onChange, onKeyDown: externalOnKeyDown, multiline, ...restProps } = props as Props & { multiline?: boolean };
    const intl = useIntl();
    // Ref is widened so both input and textarea fit. The DOM API we use
    // (selectionStart, setSelectionRange, focus, value, getBoundingClientRect)
    // is identical on both elements.
    const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
    // Fetch sequence number — used to ignore stale responses when the user
    // types fast and an earlier request resolves AFTER a later one.
    const fetchSeqRef = useRef(0);

    // ── Mention state machine ──────────────────────────────────────────────
    // mentionActive flips on when an `@` is detected in the right context,
    // off when the prefix breaks (space, newline, backspace past @, Esc).
    const [mentionActive, setMentionActive] = useState(false);
    const [mentionStart, setMentionStart] = useState<number | null>(null);
    const [mentionQuery, setMentionQuery] = useState('');
    const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);

    // ── Popover contents ───────────────────────────────────────────────────
    const [users, setUsers] = useState<MentionableUser[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(0);

    /**
     * Inspect the input value + caret position and decide whether we're in
     * mention mode. Walks backwards from the caret looking for `@`:
     *   - If we find `@` AND it's at start-of-string OR preceded by
     *     whitespace, we're in mention mode; the chars from @ to caret are
     *     the query.
     *   - If we hit a non-mention character before finding `@`, we're not
     *     in mention mode (the @ is too far back, broken by a separator).
     *   - If we never find `@`, we're not in mention mode.
     */
    const detectMention = useCallback((newValue: string, cursorPos: number) => {
        let atIdx = -1;
        for (let i = cursorPos - 1; i >= 0; i--) {
            const ch = newValue[i];
            if (ch === '@') {
                // `@` must be at the start or after whitespace. Otherwise it's
                // an email-like construct (foo@bar) and shouldn't trigger.
                if (i === 0 || /\s/.test(newValue[i - 1])) {
                    atIdx = i;
                }
                break;
            }
            if (!MENTION_CHAR_RE.test(ch)) {
                // Hit a non-mention character (space, punctuation, etc.)
                // without finding @ — not in mention mode.
                break;
            }
        }

        if (atIdx === -1) {
            return null;
        }

        return {
            start: atIdx,
            query: newValue.substring(atIdx + 1, cursorPos),
        };
    }, []);

    /**
     * Recompute the popover's anchor rectangle. Called on open and on
     * scroll/resize so the popover stays glued to the input as the page
     * moves underneath it.
     */
    const updateAnchor = useCallback(() => {
        if (!inputRef.current) return;
        setAnchorRect(inputRef.current.getBoundingClientRect());
    }, []);

    // Reposition on viewport changes while the popover is open.
    useEffect(() => {
        if (!mentionActive) return;
        const handler = () => updateAnchor();
        window.addEventListener('scroll', handler, true);
        window.addEventListener('resize', handler);
        return () => {
            window.removeEventListener('scroll', handler, true);
            window.removeEventListener('resize', handler);
        };
    }, [mentionActive, updateAnchor]);

    // Debounced fetch driven by mentionQuery. The seq guard is what
    // prevents earlier-but-late responses from clobbering newer state when
    // the user types fast.
    useEffect(() => {
        if (!mentionActive) {
            setUsers([]);
            return;
        }
        const seq = ++fetchSeqRef.current;
        setLoading(true);
        const timer = window.setTimeout(() => {
            axiosCaller('GET', `users/mentionable?q=${encodeURIComponent(mentionQuery)}`)
                .then((res: any) => {
                    if (fetchSeqRef.current !== seq) return; // stale
                    setUsers(Array.isArray(res?.data?.data) ? res.data.data : []);
                    setSelectedIndex(0);
                    setLoading(false);
                })
                .catch(() => {
                    if (fetchSeqRef.current !== seq) return;
                    setUsers([]);
                    setLoading(false);
                });
        }, FETCH_DEBOUNCE_MS);
        return () => window.clearTimeout(timer);
    }, [mentionActive, mentionQuery]);

    // Click-outside dismissal. Has to check BOTH the input and the portal
    // popover since they're in different DOM trees.
    useEffect(() => {
        if (!mentionActive) return;
        const handler = (e: MouseEvent) => {
            const target = e.target as Node | null;
            if (!target) return;
            if (inputRef.current?.contains(target)) return;
            const popover = document.querySelector('[data-mention-popover]');
            if (popover?.contains(target)) return;
            setMentionActive(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [mentionActive]);

    /**
     * Insert a user's `@username` at the mention position, restore focus,
     * and place the cursor after the inserted text + space. The space is
     * a convenience — most of the time the user wants to keep typing.
     */
    const insertMention = useCallback((user: MentionableUser) => {
        if (mentionStart === null || !inputRef.current) return;

        const currentValue = inputRef.current.value;
        const cursorPos = inputRef.current.selectionStart ?? currentValue.length;
        const before = currentValue.substring(0, mentionStart);
        const after = currentValue.substring(cursorPos);
        const insertion = `@${user.username} `;
        const newValue = before + insertion + after;

        onChange(newValue);

        const newCursorPos = mentionStart + insertion.length;
        // setSelectionRange has to happen after React commits the new value;
        // queueing on a microtask handles that.
        window.queueMicrotask(() => {
            if (!inputRef.current) return;
            inputRef.current.focus();
            try {
                inputRef.current.setSelectionRange(newCursorPos, newCursorPos);
            } catch {
                // Some browsers throw on inputs of certain types — ignore.
            }
        });

        // Reset mention state.
        setMentionActive(false);
        setMentionStart(null);
        setMentionQuery('');
        setUsers([]);
        setSelectedIndex(0);
    }, [mentionStart, onChange]);

    /**
     * Internal onChange — fans the new value out to the parent (so the
     * input stays controlled) AND inspects the value to update mention state.
     *
     * Generic over both element kinds: ChangeEvent<HTMLInputElement> and
     * ChangeEvent<HTMLTextAreaElement> are structurally identical for the
     * fields we touch (target.value, target.selectionStart).
     */
    const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const newValue = e.target.value;
        const cursorPos = e.target.selectionStart ?? newValue.length;

        onChange(newValue);

        const detection = detectMention(newValue, cursorPos);
        if (detection) {
            const isOpening = !mentionActive;
            setMentionStart(detection.start);
            setMentionQuery(detection.query);
            setMentionActive(true);
            if (isOpening) {
                updateAnchor();
            }
        } else if (mentionActive) {
            setMentionActive(false);
        }
    }, [onChange, detectMention, mentionActive, updateAnchor]);

    /**
     * Also re-evaluate mention mode when the user moves the cursor without
     * typing (arrow keys, click). Without this, you could click into the
     * middle of an `@foo` token and not get the popover.
     */
    const handleSelectionChange = useCallback(() => {
        if (!inputRef.current) return;
        const cursorPos = inputRef.current.selectionStart ?? 0;
        const detection = detectMention(inputRef.current.value, cursorPos);
        if (detection) {
            const isOpening = !mentionActive;
            setMentionStart(detection.start);
            setMentionQuery(detection.query);
            setMentionActive(true);
            if (isOpening) updateAnchor();
        } else if (mentionActive) {
            setMentionActive(false);
        }
    }, [detectMention, mentionActive, updateAnchor]);

    /**
     * Keyboard handler — eats nav keys when the popover is open, then
     * passes everything else to the external handler (so the parent's
     * "Enter to submit" still works when not in mention mode).
     *
     * Typed against the union so it works for both input and textarea call
     * sites. KeyboardEvent shape is identical between the two for the keys
     * we care about.
     */
    const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (mentionActive && users.length > 0) {
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setSelectedIndex((i) => (i + 1) % users.length);
                return;
            }
            if (e.key === 'ArrowUp') {
                e.preventDefault();
                setSelectedIndex((i) => (i - 1 + users.length) % users.length);
                return;
            }
            if (e.key === 'Enter' || e.key === 'Tab') {
                e.preventDefault();
                insertMention(users[selectedIndex]);
                return;
            }
            if (e.key === 'Escape') {
                e.preventDefault();
                setMentionActive(false);
                return;
            }
        }
        // Special case: Esc when popover is empty (still loading or no results) —
        // dismiss without firing parent handler.
        if (mentionActive && e.key === 'Escape') {
            e.preventDefault();
            setMentionActive(false);
            return;
        }
        // Cast to the caller's expected element type — TS can't see through
        // the discriminated union here but the structural shape is identical
        // for the parent handler (it only reads e.key / e.shiftKey / etc.).
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        externalOnKeyDown?.(e as any);
    }, [mentionActive, users, selectedIndex, insertMention, externalOnKeyDown]);

    // ── Popover JSX ────────────────────────────────────────────────────────
    const popover = mentionActive && anchorRect && (
        <div
            className="mention-popover"
            data-mention-popover
            role="listbox"
            style={{
                position: 'fixed',
                top: anchorRect.bottom + 6,
                left: anchorRect.left,
                minWidth: Math.max(anchorRect.width, 240),
                maxWidth: 380,
            }}
            onMouseDown={(e) => e.preventDefault()} // prevent input blur on click
        >
            {loading && users.length === 0 && (
                <div className="mention-popover__empty">
                    <FormattedMessage id="common.loading" defaultMessage="Loading…" />
                </div>
            )}
            {!loading && users.length === 0 && (
                <div className="mention-popover__empty">
                    <FormattedMessage
                        id="mention.popover.empty"
                        defaultMessage="No users match"
                    />
                </div>
            )}
            {users.map((u, i) => {
                const fullName = `${u.firstname || ''} ${u.lastname || ''}`.trim() || u.username;
                const initials = ((u.firstname?.[0] || '') + (u.lastname?.[0] || '')).toUpperCase() || u.username[0]?.toUpperCase() || '?';
                return (
                    <div
                        key={u.id}
                        role="option"
                        aria-selected={i === selectedIndex}
                        className={`mention-popover__item ${i === selectedIndex ? 'is-selected' : ''}`}
                        onMouseEnter={() => setSelectedIndex(i)}
                        onClick={() => insertMention(u)}
                    >
                        {u.avatar ? (
                            <img src={u.avatar} alt="" className="mention-popover__avatar" />
                        ) : (
                            <span className="mention-popover__initials" aria-hidden="true">{initials}</span>
                        )}
                        <span className="mention-popover__name">{fullName}</span>
                        <span className="mention-popover__username">@{u.username}</span>
                    </div>
                );
            })}
        </div>
    );

    // Shared props passed to either <input> or <textarea>. We rely on the
    // structural similarity between the two for selection/value APIs. Cast
    // the ref accordingly per branch — TS can't unify the two ref types
    // through a single JSX expression.
    const sharedHandlers = {
        value,
        onChange: handleChange,
        onKeyDown: handleKeyDown,
        onKeyUp: handleSelectionChange,
        onClick: handleSelectionChange,
        autoComplete: 'off',
        'aria-label': (restProps as { 'aria-label'?: string })['aria-label'] || intl.formatMessage({
            id: 'mention.input.ariaLabel',
            defaultMessage: 'Comment input. Type @ to mention a user.',
        }),
    };

    return (
        <>
            {multiline ? (
                <textarea
                    ref={(el) => { inputRef.current = el; }}
                    {...(restProps as TextareaHTMLAttributes<HTMLTextAreaElement>)}
                    {...sharedHandlers}
                />
            ) : (
                <input
                    ref={(el) => { inputRef.current = el; }}
                    {...(restProps as InputHTMLAttributes<HTMLInputElement>)}
                    {...sharedHandlers}
                />
            )}
            {popover && createPortal(popover, document.body)}
        </>
    );
};

export default MentionInput;
